// Onboarding: logo depois do cadastro, a pessoa diz como vai usar a plataforma (comprar e/ou vender
// processamento) e, se for comprar, o tipo de consumo padrão. POST /api/client/onboarding.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, WORKER_APP_URL, type Me, type TierId } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import { Wordmark } from '../components/Brand';
import '../styles/onboarding.css';

type Profile = 'buyer' | 'seller' | 'both';
const x = (v: number) => String(v).replace('.', ',');

// mini-diagramas: pontos âmbar = a sua carga; cinza = de outros clientes
const Graphic = ({ id }: { id: TierId }) => {
  switch (id) {
    case 'shared': {
      const pts: [number, number, number][] = [[14, 14, 1], [14, 50, 0], [60, 8, 0], [106, 14, 1], [106, 50, 0], [60, 56, 1]];
      return <svg viewBox="0 0 120 64" aria-hidden="true"><g className="ln">{pts.map(([a, b]) => <line key={`${a}-${b}`} x1="60" y1="32" x2={a} y2={b} />)}</g><circle className="hub" cx="60" cy="32" r="7" />{pts.map(([a, b, y]) => <circle key={`${a}-${b}`} className={y ? 'you' : 'oth'} cx={a} cy={b} r="5" />)}</svg>;
    }
    case 'priority':
      return <svg viewBox="0 0 120 64" aria-hidden="true"><path className="ln" d="M8 32 H96" /><path className="arr" d="M96 24 L110 32 L96 40" />{[22, 40, 58].map((cx) => <circle key={cx} className="oth" cx={cx} cy="32" r="6" />)}<circle className="you" cx="80" cy="32" r="8" /></svg>;
    case 'dedicated':
      return <svg viewBox="0 0 120 64" aria-hidden="true"><rect className="fence" x="52" y="8" width="62" height="48" rx="8" />{([[66, 22], [84, 22], [102, 22], [66, 42], [84, 42], [102, 42]] as [number, number][]).map(([a, b]) => <circle key={`${a}-${b}`} className="you" cx={a} cy={b} r="5" />)}{([[14, 16], [30, 32], [14, 48]] as [number, number][]).map(([a, b]) => <circle key={`${a}-${b}`} className="oth" cx={a} cy={b} r="5" />)}</svg>;
    case 'onprem':
      return <svg viewBox="0 0 120 64" aria-hidden="true"><path className="house" d="M30 30 L60 8 L90 30 V58 H30 Z" />{([[48, 38], [60, 38], [72, 38], [54, 50], [66, 50]] as [number, number][]).map(([a, b]) => <circle key={`${a}-${b}`} className="you" cx={a} cy={b} r="4.5" />)}</svg>;
  }
};

const TIERS: { id: TierId; name: string; hook: string; body: string; fit: string; note?: string }[] = [
  { id: 'shared', name: 'Compartilhado', hook: 'A rede inteira trabalhando por você.', body: 'Suas requisições entram na fila comum e vão para a máquina livre mais adequada. É o menor custo por token.', fit: 'começar, testes, lotes e tarefas sem urgência' },
  { id: 'priority', name: 'Prioritário', hook: 'Passe na frente da fila.', body: 'A mesma rede, mas as suas requisições são atendidas antes das compartilhadas — menos espera nos horários de pico.', fit: 'produtos em produção e conversas com usuários' },
  { id: 'dedicated', name: 'Dedicado', hook: 'Máquinas separadas da rede comum.', body: 'A Clusterizze cadastra uma pool dedicada e a associa à sua conta — máquinas que não atendem a rede compartilhada, só quem está associado à pool. Desempenho previsível e isolamento, sem você gerenciar máquina nenhuma.', fit: 'volume constante, cargas críticas e SLA', note: 'a pool é cadastrada e associada pela plataforma; você a escolhe ao criar a chave' },
  { id: 'onprem', name: 'On-premise', hook: 'Seus dados não saem de casa.', body: 'A Clusterizze cadastra uma pool on-premise para você e o processamento acontece só nas máquinas que você conectar nela, na sua infraestrutura. Mesma API, mesmo painel, sem máquinas de terceiros.', fit: 'dados sensíveis, LGPD e compliance', note: 'a pool é cadastrada e associada pela plataforma; você conecta as suas máquinas nela em Workers' },
];

function priceOf(me: Me, id: TierId) {
  const m = (me.tiers ?? []).find((t) => t.id === id)?.multiplier ?? { shared: 1, priority: 1.5, dedicated: 2, onprem: 0.2 }[id];
  return id === 'shared' ? 'preço base por token' : id === 'onprem' ? `${x(m)}× — só a taxa da plataforma` : `${x(m)}× o preço base`;
}

export function Onboarding() {
  const { me, refresh } = useMeRequired();
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tier, setTier] = useState<TierId>('shared');
  const first = (me.name || me.email || '').split(/[ @]/)[0];

  const finish = async (p: Profile, t: TierId) => {
    try {
      await api('/api/client/onboarding', { method: 'POST', body: JSON.stringify({ profile: p, tier: t }) });
      await refresh();
      if (p === 'seller' || t === 'onprem') {
        if (t === 'onprem') toast('On-premise: quando a plataforma associar a sua pool, conecte as suas máquinas nela');
        window.location.href = WORKER_APP_URL;   // máquinas ficam no app de workers
      } else nav('/chaves', { replace: true });
    } catch (e) { toast((e as Error).message, 'err'); }
  };

  const pick = (p: Profile) => { setProfile(p); if (p === 'seller') void finish('seller', 'shared'); else setStep(2); };

  if (step === 1) return (
    <div className="ob"><div className="ob-in">
      <header className="ob-top"><span className="ob-mark"><Wordmark size="sm" /></span><span className="ob-step">passo 1 de 2</span></header>
      <p className="ob-eyebrow">Bem-vindo{first ? `, ${first}` : ''}</p>
      <h1>Como a Clusterizze vai trabalhar para você?</h1>
      <p className="ob-lead">Escolha o ponto de partida. Uma conta faz as duas coisas — dá para mudar depois.</p>
      <div className="ob-paths">
        <button className="ob-path" onClick={() => pick('buyer')}><span className="ob-k">Comprar processamento</span>
          <b>Coloque IA no seu produto sem comprar uma GPU</b>
          <p>Chame modelos abertos como Llama, Qwen e DeepSeek por uma API simples e pague só pelos tokens que usar, em reais. Sem servidor para manter, sem contrato mínimo.</p>
          <ul><li>Pague por uso, com saldo em reais</li><li>Modelos abertos, sem ficar preso a um fornecedor</li><li>Da rede compartilhada ao on-premise, conforme a sensibilidade dos dados</li></ul>
          <span className="ob-go">Quero usar IA pela API <i>→</i></span></button>
        <button className="ob-path" onClick={() => pick('seller')}><span className="ob-k">Vender processamento</span>
          <b>Faça a sua GPU parada trabalhar e render</b>
          <p>Instale um agente leve com um comando, conecte a máquina e receba por cada token que ela gerar. Você acompanha ganhos, uso e energia ao vivo.</p>
          <ul><li>Instalação em um comando, sem configuração</li><li>Você escolhe os modelos e o limite de uso da GPU</li><li>Relatório de ganho líquido: repasse menos energia</li></ul>
          <span className="ob-go">Quero colocar minhas máquinas <i>→</i></span></button>
      </div>
      <button className="ob-both" onClick={() => pick('both')}>Quero fazer os dois: usar a API e também colocar máquinas</button>
    </div></div>
  );

  const chosen = TIERS.find((t) => t.id === tier) ?? TIERS[0];
  return (
    <div className="ob"><div className="ob-in">
      <header className="ob-top"><span className="ob-mark"><Wordmark size="sm" /></span><button className="ob-back" onClick={() => setStep(1)}>← voltar</button><span className="ob-step">passo 2 de 2</span></header>
      <p className="ob-eyebrow">{profile === 'both' ? 'Comprar e vender processamento' : 'Comprar processamento'}</p>
      <h1>Como você quer processar os seus dados?</h1>
      <p className="ob-lead">Este é o padrão da sua conta e do chat de teste. Cada chave de API pode usar um tipo diferente — você escolhe ao criá-la.</p>
      <div className="ob-tiers" role="radiogroup" aria-label="Tipo de processamento">
        {TIERS.map((t) => (
          <button key={t.id} className={`ob-tier ${tier === t.id ? 'on' : ''}`} role="radio" aria-checked={tier === t.id} onClick={() => setTier(t.id)}>
            <span className="ob-g"><Graphic id={t.id} /></span>
            <span className="ob-tn">{t.name}</span><b>{t.hook}</b><p>{t.body}</p>
            <span className="ob-fit">Ideal para {t.fit}</span>
            <span className="ob-price">{priceOf(me, t.id)}{t.note && <small>{t.note}</small>}</span>
          </button>
        ))}
      </div>
      <div className="ob-foot"><span className="mute">Pode mudar depois em Conta ou por chave de API.</span><button className="btn primary" onClick={() => finish(profile ?? 'buyer', tier)}>Continuar com {chosen.name.toLowerCase()} →</button></div>
    </div></div>
  );
}

